import React, { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react";
import { NavigateFunction } from "react-router-dom";
import PlusIcon from "../../../../../images/common/add-inside-circle-blue.svg";
import {
  ContactReferenceModel,
  CountryWithStateModel,
  MerchantAccount,
  PaymentMethodModel,
  UpsertPaymentModel,
} from "../models";
import {
  ContactService,
  MerchantAccountService,
  PaymentMethodService,
  PaymentService,
} from "../services";
import { ApplicationStatus, BillingMethodType } from "../enums";
import { DescriptionCell, InvoicePopupWrapper } from "../styles";
import {
  AuthService,
  CustomLabel,
  EditableField,
  FlexContainer,
  IAPIResponse,
  Input,
  Loader,
  ModalComponent,
  SapphireBlueButton,
  TextInputWithDropdown,
  TimeAMPM,
  ToastService,
  UtilityMethods,
  withRouter,
} from "@mindmemobile/mm-shared-global";
import { IAPICreateEditPaymentResponseModel } from "../interfaces";
import RenderContactInfo from "./RenderContactInfo";
import ContactDropDown from "./ContactDropDown";
import MerchantAccountsDropDown from "./MerchantAccountsDropDown";
import BillingMethods from "./BillingMethods";
import { AccountSettingsStore } from "../store";
import PaymentMethodDropdown from "./PaymentMethodDropdown";
import { NMIPaymentMethodFormHandle } from "./NMIPaymentMethodForm";

type IProps = {
  contactId?: string;
  navigate: NavigateFunction;
  onClose: (refresh: boolean, contactId?: string) => void;
  paymentData: UpsertPaymentModel;
  countriesWithState: CountryWithStateModel[];
};

const UpsertPaymentPopUp: React.FC<IProps> = ({
  onClose,
  paymentData,
  contactId,
  countriesWithState,
}) => {
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethodModel[]>(
    [],
  );
  const [merchantAccounts, setMerchantAccounts] = useState<MerchantAccount[]>(
    [],
  );
  const [data, setPaymentData] = React.useState<UpsertPaymentModel>(
    new UpsertPaymentModel({
      ...paymentData,
      isContactRequired:
        !contactId && AuthService.isMindMeContactRoute ? false : true,
    }),
  );
  const [isAPIRequestPending, setAPIRequestPending] = React.useState(false);
  const [showDescription, setShowDescription] = React.useState(false);
  const [showLoader, setShowLoader] = React.useState(true);
  const [contacts, setContactsData] = React.useState<ContactReferenceModel[]>(
    [],
  );
  const [statementDescription, setStatementDescriptor] = useState({
    isEdit: false,
    value: "",
  });
  const [hasMoreContacts, setHasMoreContacts] = React.useState(false);
  const formRef = useRef<NMIPaymentMethodFormHandle>(null);

  const [shouldSubmit, setShouldSubmit] = useState(false);
  useEffect(() => {
    if (shouldSubmit) {
      onSubmit(true);
      setShouldSubmit(false); // reset
    }
  }, [shouldSubmit]);

  function isUpdate() {
    return data.id !== "";
  }

  useEffect(() => {
    const loadData = async () => {
      try {
        setShowLoader(true);

        const paymentMethodList = await fetchPaymentMethods();
        const defaultMerchantAccount = await loadMerchantAccounts(
          paymentMethodList,
          !paymentData?.id,
        );

        await loadContacts(
          defaultMerchantAccount,
          data.contact?.id,
          data.contact?.mindmeContactId,
          paymentMethodList,
        );
        // await Promise.all([
        //   loadMerchantAccounts(paymentMethodList),
        //   loadContacts(
        //     data.contact?.id,
        //     data.contact?.mindmeContactId,
        //     paymentMethodList
        //   ),
        // ]);
      } catch (error) {
        console.error("Error in API calls:", error);
      } finally {
        setShowLoader(false);
      }
    };

    loadData();
  }, []);

  async function fetchPaymentMethods() {
    try {
      const response =
        await PaymentMethodService.getPaymentMethods().toPromise();

      if (response?.result) {
        const paymenMethods = response.result.map((paymentMethod) =>
          PaymentMethodModel.deserializeAPIModel(paymentMethod),
        );
        setPaymentMethods(paymenMethods);

        const selectedPaymentMethod =
          PaymentMethodModel.getDefaultSelecedPaymentMethod(
            paymenMethods,
            data.contact?.id || "",
            data.merchantAccount?.merchantAccountId || "",
          );

        if (selectedPaymentMethod && data && !data.paymentMethod?.id) {
          setPaymentData(
            (prev) =>
              new UpsertPaymentModel({
                ...prev,
                paymentMethod: !prev.paymentMethod
                  ? selectedPaymentMethod
                  : prev.paymentMethod,
              }),
          );
        }
        return paymenMethods;
      }
    } catch (error) {
      console.error("Error fetching payment methods:", error);
      throw error;
    }
  }

  async function loadContacts(
    defaultMerchantAccount?: MerchantAccount,
    paymentContactId?: string,
    mindmeContactId?: string,
    paymenMethods?: PaymentMethodModel[],
    searchValue?: string,
    pageSize: number = 50,
  ) {
    try {
      const response = await ContactService.getMindMeContacts(
        AuthService.authData.accountId,
        paymentContactId,
        mindmeContactId,
        searchValue,
        pageSize,
      ).toPromise();

      let contacts =
        response?.result?.data?.map((contact) => {
          return ContactReferenceModel.deserializeFromMindMeContacts(contact);
        }) || [];
      contacts = contacts.filter((c) => c.displayValue.trim() !== "");

      setContactsData(contacts);
      setHasMoreContacts(response?.result?.hasMoreData || false);
      const selectedContact = contacts.find(
        (c) =>
          (paymentContactId && c.id === paymentContactId) ||
          (mindmeContactId && c.mindmeContactId === mindmeContactId),
      );
      let defaultPaymentMethod: PaymentMethodModel | undefined =
        data.paymentMethod;
      if (paymenMethods && !data.paymentMethod) {
        defaultPaymentMethod =
          PaymentMethodModel.getDefaultSelecedPaymentMethod(
            paymenMethods,
            paymentContactId || "",
            defaultMerchantAccount?.merchantAccountId || "",
          );
      }
      if (selectedContact) {
        setPaymentData(
          (prevData) =>
            new UpsertPaymentModel({
              ...prevData,
              contact: selectedContact,
              isNewPaymentMethod:
                !defaultPaymentMethod && !paymentData?.id ? true : false,
              newMethodIsPrimary:
                !defaultPaymentMethod && !paymentData?.id ? true : false,
            }),
        );
      }
    } catch (error) {
      console.error("Error loading contacts:", error);
      throw error;
    }
  }

  async function loadMerchantAccounts(
    paymentMethodList: PaymentMethodModel[] | undefined,
    setDefaultMerchantAccount: boolean,
  ) {
    try {
      const response = await MerchantAccountService.getMerchantAccounts(
        AuthService.authData.accountId,
        1,
        10000,
        "",
      ).toPromise();

      if (response?.result) {
        const activeMerchantAccounts = (
          response.result?.map((m) => MerchantAccount.deserializeAPIModel(m)) ||
          []
        ).filter((f) => f.applicationStatus === ApplicationStatus.Active);

        const defaultMerchantAccount =
          MerchantAccount.getDefaultMerchantAccount(activeMerchantAccounts);

        const selectedPaymentMethod =
          data.paymentMethod ||
          PaymentMethodModel.getDefaultSelecedPaymentMethod(
            paymentMethodList || [],
            data.contact?.id || "",
            defaultMerchantAccount?.merchantAccountId || "",
          );
        setPaymentData(
          (prevData) =>
            new UpsertPaymentModel({
              ...prevData,
              merchantAccount: setDefaultMerchantAccount
                ? defaultMerchantAccount
                : prevData.merchantAccount,
              paymentMethod: selectedPaymentMethod,
              isNewPaymentMethod: !selectedPaymentMethod ? true : false,
              newMethodIsPrimary: !selectedPaymentMethod ? true : false,
            }),
        );

        setMerchantAccounts(activeMerchantAccounts);
        return setDefaultMerchantAccount
          ? defaultMerchantAccount
          : data.merchantAccount;
      }
    } catch (error) {
      console.error("Error loading merchant accounts:", error);
      throw error;
    }
    return data.merchantAccount;
  }

  const formatAmount = (value: string): string => {
    const sanitized = AccountSettingsStore.sanitizeAmount(value.toString());
    if (parseFloat(sanitized) < 0 || isNaN(parseFloat(sanitized))) {
      return "0.00";
    }
    return AccountSettingsStore.formatAmountWithoutCommas(
      parseFloat(sanitized || "0.00"),
      true,
    );
  };

  function row(label: string, rightSide: React.ReactNode, className?: string) {
    return (
      <FlexContainer spacing={30} margin="15px" className={className}>
        <CustomLabel>{label}</CustomLabel>
        <FlexContainer alignItems="center" spacing={20}>
          {rightSide}
        </FlexContainer>
      </FlexContainer>
    );
  }

  function updateState(
    key: keyof UpsertPaymentModel,
    value:
      | string
      | boolean
      | number
      | Date
      | MerchantAccount
      | PaymentMethodModel,
    isBlur?: boolean,
  ) {
    const clonedData = new UpsertPaymentModel(data);
    switch (key) {
      case "description":
        clonedData.description = value as string;
        break;
      case "amount":
        if (isBlur) {
          value = formatAmount(value as string);
        }
        clonedData.amount = value as string;
        break;
      case "billingMethod":
        clonedData.billingMethod = value as keyof typeof BillingMethodType;
        break;
      case "dueDate":
        clonedData.dueDate = value as string;
        break;
      case "paymentMethod":
        clonedData.paymentMethod = value as PaymentMethodModel;
        break;
      case "merchantAccount":
        const newMerchantAccount = value as MerchantAccount;
        if (
          clonedData.merchantAccount?.merchantAccountId !=
          newMerchantAccount.merchantAccountId
        ) {
          const defaultPaymentMethod =
            PaymentMethodModel.getDefaultSelecedPaymentMethod(
              paymentMethods,
              clonedData.contact?.id || "",
              newMerchantAccount?.merchantAccountId || "",
            );
          const isNew = !defaultPaymentMethod;
          clonedData.isNewPaymentMethod = isNew;
          clonedData.newMethodIsPrimary = isNew;
          clonedData.paymentMethod = defaultPaymentMethod;
        }
        clonedData.merchantAccount = newMerchantAccount;
        break;
      case "newMethodIsPrimary":
        clonedData.newMethodIsPrimary = value as boolean;
        break;
      case "emailReceipt":
        clonedData.emailReceipt = value as boolean;
        break;
      case "statementDescriptor":
        clonedData.statementDescriptor = value as string;
        setStatementDescriptor({ isEdit: false, value: value as string });
        break;
    }
    clonedData.validate(true);
    setPaymentData(clonedData);
  }

  function textInput(
    type: "text" | "textarea" | "number" | "email" | "date",
    key: keyof UpsertPaymentModel,
    value: string | Date,
    placeHolder: string,
    className: string,
  ) {
    return (
      <Input
        type={type}
        value={value}
        error={data.hasError(key)}
        className={className}
        onChange={(e, isBlur) => updateState(key, e.target.value, isBlur)}
        placeholder={placeHolder}
        onDateChange={(date) => updateState(key, date)}
        minDate={new Date()}
      />
    );
  }

  function onSubmit(hasValidNMIPaymentMethod = false) {
    const success = data.validate(false);
    if (!success) {
      setPaymentData(new UpsertPaymentModel(data));
      return;
    }
    //ONLY DO IT FOR NMI AS FOR NMI WE NEED TO WAIT FOR PAYMENT TOKEN AFTER validateCardDetails
    if (
      !hasValidNMIPaymentMethod &&
      data.merchantAccount?.isNMIMerchantAccount() &&
      data.isNewPaymentMethod
    ) {
      formRef.current?.validateCardDetails();
      return;
    }
    saveUpdatePayment();
  }

  function saveUpdatePayment() {
    setAPIRequestPending(true);
    const errorMessage = data.id ? "Error updating payment" : "Payment Failed";
    const successResposne = (
      response: IAPIResponse<IAPICreateEditPaymentResponseModel>,
    ) => {
      setAPIRequestPending(false);
      if (response.result.success) {
        let close = true;
        const successMessage = data.id
          ? "Payment updated Successfully"
          : "Payment Successful";
        if (response.result.isPaymentFailed) {
          ToastService.showError("Payment Failed");
          close = false;
        } else if (response.result.isPaid) {
          ToastService.showSuccess("Payment Successful");
        } else {
          ToastService.showSuccess(successMessage);
        }
        if (close) {
          onClose(true, response.result.contactId);
        } else {
          setAPIRequestPending(false);
        }
      } else {
        switch (response.result.failureReason) {
          case "AlreadyPaid":
            ToastService.showError("Payment already paid");
            break;
          case "AboutToProcess":
            ToastService.showError("Payment is about to process");
            break;
          case "MerchantAccountNotFound":
            ToastService.showError("Merchant account not found");
            break;
        }
        setAPIRequestPending(false);
        ToastService.showError(errorMessage);
      }
    };
    const errorResponse = (error: any) => {
      console.error(error);
      setAPIRequestPending(false);
      ToastService.showError(errorMessage);
    };
    const mindmeContactId = AuthService.isMindMeContactRoute
      ? AuthService.getMindMeContactId
      : data.contact?.mindmeContactId || "";
    if (data.id) {
      const updateRequest = data.createPaymentAPIModel(
        data.contact?.id || "",
        mindmeContactId,
      );
      PaymentService.updatePayment(updateRequest).subscribe({
        next: successResposne,
        error: errorResponse,
      });
    } else {
      const request = data.createPaymentAPIModel(
        data.contact?.id || "",
        mindmeContactId,
      );
      PaymentService.savePayment(request).subscribe({
        next: successResposne,
        error: errorResponse,
      });
    }
  }

  function amountRow() {
    return (
      <>
        {row(
          "Payment Amount",
          <CustomLabel>
            {textInput("text", "amount", data.amount, "Enter amount", "")}
          </CustomLabel>,
        )}
      </>
    );
  }

  function updateTime(amPm: keyof typeof TimeAMPM, time: string) {
    const clonedData = new UpsertPaymentModel(data);
    clonedData.time = time;
    clonedData.amPm = amPm;
    clonedData.validate(true);
    setPaymentData(clonedData);
  }

  function onContactChange(selectedContact: ContactReferenceModel) {
    const defaultPaymentMethod =
      PaymentMethodModel.getDefaultSelecedPaymentMethod(
        paymentMethods,
        selectedContact.id,
        data.merchantAccount?.merchantAccountId || "",
      );
    setPaymentData(
      new UpsertPaymentModel({
        ...data,
        contact: selectedContact,
        paymentMethod: defaultPaymentMethod,
        newMethodIsPrimary: !defaultPaymentMethod ? true : false,
        isNewPaymentMethod: !defaultPaymentMethod ? true : false,
      }),
    );
  }

  const onRefreshContacts = (
    contactId: string,
    mindmeContactId: string,
    searchValue: string,
    pageSize: number,
  ) => {
    loadContacts(
      data.merchantAccount,
      contactId,
      mindmeContactId,
      paymentMethods,
      searchValue,
      pageSize,
    );
  };

  function body() {
    if (showLoader) return new Loader(true).loaderComponent;

    const isShowDescription =
      showDescription ||
      data.hasError("description") ||
      data.description.length > 0;

    return (
      <InvoicePopupWrapper className="subscription-popup">
        {!AuthService.isMindMeContactRoute && (
          <>
            {isUpdate() &&
              data.contact &&
              row("Customer", <RenderContactInfo contact={data.contact} />)}
            {(!data.contact || !isUpdate()) &&
              row(
                "Customer",
                <ContactDropDown
                  contacts={contacts}
                  selectedContact={data.contact}
                  hasError={data.hasError("contact")}
                  onSelect={(selectedContact) => {
                    onContactChange(selectedContact);
                  }}
                  hasMoreContacts={hasMoreContacts}
                  countriesWithState={countriesWithState}
                  onRefresh={(
                    contactId,
                    mindmeContactId,
                    searchValue,
                    pageSize,
                  ) => {
                    onRefreshContacts(
                      contactId,
                      mindmeContactId,
                      searchValue,
                      pageSize,
                    );
                  }}
                  className="white-bg"
                />,
              )}
          </>
        )}
        {row(
          "Billing Method",
          <BillingMethods
            selectedMethod={data.billingMethod}
            onChange={(value) => updateState("billingMethod", value)}
          />,
        )}
        {merchantAccounts.length > 1 &&
          row(
            "Merchant Account",
            <MerchantAccountsDropDown
              selectedMerchantAccount={data.merchantAccount}
              merchantAccounts={merchantAccounts}
              showError={data.hasError("merchantAccount")}
              onChange={(option) => updateState("merchantAccount", option)}
              className="white-bg "
            />,
          )}
        {row(
          "Payment Method",
          <PaymentMethodDropdown
            ref={formRef}
            merchantAccount={
              data.merchantAccount?.merchantAccountId
                ? data.merchantAccount
                : merchantAccounts[0]
            }
            selectedPaymentMethod={data.paymentMethod}
            paymentMethods={paymentMethods}
            contactId={data.contact?.id || data.contact?.mindmeContactId || ""}
            isRenderWithoutContact={
              (!contactId && AuthService.isMindMeContactRoute) ||
              data.contact?.mindmeContactId
                ? true
                : false
            }
            onChange={(option) => updateState("paymentMethod", option)}
            hasError={data.hasError("paymentMethod")}
            className="invoice-payment-method"
            hasErrorInNewPaymentMethod={data.hasError(
              "newPaymentGatewayMethodId",
            )}
            onNewPaymentMethod={(
              isNewPaymentMethod,
              newPaymentGatewayMethodId,
            ) => {
              onNewPaymentMethod(isNewPaymentMethod, newPaymentGatewayMethodId);
            }}
            isNewPaymentMethodPrimary={data.newMethodIsPrimary}
            updateNewMethodAsPrimary={(value: boolean) =>
              updateState("newMethodIsPrimary", value)
            }
            isLodingInProcess={showLoader}
            headerClass="subscription-payment-method"
          />,
        )}
        {amountRow()}
        {row(
          "Payment Date",
          <>
            {textInput(
              "date",
              "dueDate",
              UtilityMethods.toLocalISOString(new Date(data.dueDate)),
              "Enter due date",
              "invoice-payment-datepicker",
            )}
            {data.dueDate &&
              new Date().toDateString() !==
                new Date(data.dueDate).toDateString() && (
                <TextInputWithDropdown
                  textInputValue={data.time}
                  dropdownValue={data.amPm}
                  onTextChange={(value) => updateTime(data.amPm, value)}
                  onDropdownChange={(option) =>
                    updateTime(option.value, data.time)
                  }
                  className="invoice-timezone subscription-timezone"
                  dropdownOptions={[
                    Object.keys(TimeAMPM).map((key) => ({
                      value: key,
                      label: TimeAMPM[key as keyof typeof TimeAMPM],
                    })),
                  ]}
                  error={data.hasError("time")}
                  placeholderForTextInput="hh:mm"
                  isTime={true}
                />
              )}
            <FlexContainer alignItems="center">
              <CustomLabel>
                {AccountSettingsStore.generalSettings.timeZone.abbreviation ||
                  ""}
              </CustomLabel>
            </FlexContainer>
          </>,
        )}
        {row(
          "Description",
          isShowDescription ? (
            <CustomLabel>
              {textInput(
                "textarea",
                "description",
                data.description,
                "Enter Description",
                "",
              )}
            </CustomLabel>
          ) : (
            <FlexContainer
              alignItems="center"
              spacing={10}
              className="clickable"
            >
              <img
                src={PlusIcon}
                alt="description"
                className="desc-plus-icon"
                onClick={() => setShowDescription(true)}
              />
              <CustomLabel
                color="#556BB8"
                className="clickable"
                lineHeight="40px"
                onClick={() => setShowDescription(true)}
              >
                Add
              </CustomLabel>
            </FlexContainer>
          ),
          isShowDescription
            ? "desc-row-margin"
            : "desc-row-margin align-center",
        )}
        {/* {row(
          "Statement Descriptor",
          <DescriptionCell>
            <EditableField
              value={statementDescription.value || data.statementDescriptor}
              isEdit={statementDescription.isEdit}
              onEditClick={() =>
                setStatementDescriptor({
                  isEdit: true,
                  value: data.statementDescriptor,
                })
              }
              onSaveClick={() =>
                updateState("statementDescriptor", statementDescription.value)
              }
              onCancelClick={() =>
                setStatementDescriptor({ isEdit: false, value: "" })
              }
              onChange={(e) =>
                setStatementDescriptor({ isEdit: true, value: e.target.value })
              }
              placeholder="Enter Name"
            />
          </DescriptionCell>,
          statementDescription.isEdit ? "" : "align-center"
        )} */}
        {row(
          "Email payment receipt",
          <CustomLabel lineHeight="40px" className="save-draft-chkbx">
            <Input
              id="emailReceiptCheckbox"
              type="checkbox"
              value={data.emailReceipt}
              onChange={(e) => updateState("emailReceipt", e.target.checked)}
              label={" "}
            />
          </CustomLabel>,
          "email-receipt-chkbx",
        )}
      </InvoicePopupWrapper>
    );
  }

  function onNewPaymentMethod(isNewPaymentMethod: boolean, token: string) {
    if (
      isNewPaymentMethod === data.isNewPaymentMethod &&
      token === data.newPaymentGatewayMethodId
    )
      return;
    setPaymentData(
      (prevData) =>
        new UpsertPaymentModel({
          ...prevData,
          isNewPaymentMethod: isNewPaymentMethod,
          newPaymentGatewayMethodId: token,
        }),
    );

    if (
      token &&
      isNewPaymentMethod &&
      data.merchantAccount?.isNMIMerchantAccount()
    ) {
      setShouldSubmit(true);
    }
  }
  return (
    <ModalComponent
      title={isUpdate() ? "Modify Scheduled Payment" : "New Payment"}
      titleFontSize="22px"
      bodyPadding="40px 50px"
      onClose={() => onClose && onClose(false)}
      showFooter={true}
      wrapperWidth="900px"
      bodyBackgroundColor="#fff"
      footerBackground="#F4F7FB"
      footerHeight="auto"
      footerContent={
        <FlexContainer alignItems="center" justifyContent="center" spacing={15}>
          <SapphireBlueButton
            minWidth="120px"
            height="30px"
            onClick={() => onClose(false)}
          >
            Cancel
          </SapphireBlueButton>
          <SapphireBlueButton
            disabled={isAPIRequestPending}
            primary
            minWidth="120px"
            height="30px"
            onClick={() => onSubmit()}
          >
            {data.isShowPayButton()
              ? "Pay Now"
              : isUpdate()
                ? "Update"
                : "Schedule"}
          </SapphireBlueButton>
        </FlexContainer>
      }
    >
      {body()}
    </ModalComponent>
  );
};

export default withRouter(observer(UpsertPaymentPopUp));
